import type { ReactNode } from 'react'

import { useDimensions } from '../hooks/useDimensions'

type TResponsiveDimensions = {
  children: (values: ReturnType<typeof useDimensions>) => ReactNode
}
/**
 * ResponsiveDimensions is a component that calls its children with the result of useDimensions.
 * Usage :
 *
 *  <ResponsiveDimensions>
 *      {({ xs }) => (<MyComponent />)
 *  </ResponsiveDimensions>
 *
 * The children should be a function that takes an object with the following properties:
 * - width: The width of the window.
 * - height: The height of the window.
 * - xs: A boolean indicating if the window is in xs size.
 * - sm: A boolean indicating if the window is in sm size.
 * - md: A boolean indicating if the window is in md size.
 * - lg: A boolean indicating if the window is in lg size.
 * - xl: A boolean indicating if the window is in xl size.
 * The children should return a ReactNode.
 *
 * @param {TResponsiveDimensions} props The props of the component.
 * @param {Function} props.children A function that takes the result of useDimensions and returns a ReactNode.
 * @returns {ReactNode} The result of the children function.
 */
export const ResponsiveDimensions = ({ children }: TResponsiveDimensions): ReactNode => {
  const result = useDimensions()
  return children(result)
}
